import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
      { path: 'inbox', name: 'inbox', component: () => import('@/views/InboxView.vue') },
      { path: 'capture', name: 'capture', component: () => import('@/views/CaptureView.vue') },
      { path: 'work', name: 'work', component: () => import('@/views/WorkQueueView.vue') },
      { path: 'work/:id', name: 'work-item', component: () => import('@/views/WorkItemDetailView.vue') },
      { path: 'customers', name: 'customers', component: () => import('@/views/CustomersView.vue') },
      {
        path: 'customers/:id',
        name: 'customer',
        component: () => import('@/views/CustomerDetailView.vue'),
      },
      { path: 'products', name: 'products', component: () => import('@/views/ProductsView.vue') },
      {
        path: 'products/:id',
        name: 'product',
        component: () => import('@/views/ProductDetailView.vue'),
      },
      {
        path: 'solutions/:id',
        name: 'solution',
        component: () => import('@/views/CustomerSolutionDetailView.vue'),
      },
      { path: 'knowledge', name: 'knowledge', component: () => import('@/views/KnowledgeView.vue') },
      { path: 'improvements', name: 'improvements', component: () => import('@/views/ImprovementsView.vue') },
      { path: 'reports', name: 'reports', component: () => import('@/views/ReportsView.vue') },
      {
        path: 'members',
        name: 'members',
        component: () => import('@/views/MembersView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'settings/integrations',
        name: 'integrations',
        component: () => import('@/views/IntegrationsView.vue'),
        meta: { requiresAdmin: true },
      },
      { path: 'settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
    ],
  },
  {
    path: '/portal',
    component: () => import('@/layouts/PortalShell.vue'),
    meta: { requiresAuth: true, portal: true },
    children: [
      {
        path: '',
        name: 'portal',
        component: () => import('@/views/portal/PortalEnquiriesView.vue'),
      },
      {
        path: 'enquiries/:id',
        name: 'portal-enquiry',
        component: () => import('@/views/portal/PortalEnquiryView.vue'),
      },
    ],
  },
  // Signing in to the portal: a code by email, no password. The plain page works for anybody on
  // record; the customer's own link (/portal/mrbeef) names whose portal it is.
  {
    path: '/portal/sign-in',
    name: 'portal-sign-in',
    component: () => import('@/views/portal/PortalSignInView.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/:customerCode',
    name: 'portal-entry',
    component: () => import('@/views/portal/PortalSignInView.vue'),
    meta: { public: true },
  },
  {
    path: '/set-password',
    name: 'set-password',
    component: () => import('@/views/SetPasswordView.vue'),
    meta: { public: true },
  },
  // Both halves use it: a customer contact who forgot their password has the same problem as a
  // colleague, and redeems the same link.
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // One session probe on first navigation; afterwards the store answers from memory.
  await auth.restore()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    // A customer who is not signed in goes to the portal's own sign-in, not the staff one.
    if (to.meta.portal) return { name: 'portal-sign-in', query: { redirect: to.fullPath } }
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  // The two halves are kept apart here as well as on the server. This is only so nobody lands on a
  // page that answers 403 to everything — the policies are what actually enforce it, and a guard in
  // the browser enforces nothing.
  if (auth.isCustomerContact && to.meta.requiresAuth && !to.meta.portal) {
    return { name: 'portal' }
  }

  // Staff may look at the portal only as a preview of one customer (?as=...), which reads through
  // staff endpoints. Without that, the portal pages would answer 403 to a staff session.
  if (to.meta.portal && auth.isAuthenticated && !auth.isCustomerContact && !to.query.as) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta.public && auth.isAuthenticated && to.name === 'login') {
    return { name: auth.isCustomerContact ? 'portal' : 'dashboard' }
  }

  return true
})
